import useInput from '@hooks/useInput';
import useToggle from '@hooks/useToggle';
import type { MapLocation } from '@types';
import { useEffect, useRef } from 'react';
import styled from 'styled-components';
import Button from '../Button';
import Input from '../Input';
import Modal from '../Modal';
import TextLink from '../TextLink';
import Title from '../Title';

interface Props {
  defaultLocation?: MapLocation;
  onRequestClose: () => void;
  onConfirm: (location: MapLocation) => void;
}

const StyledModal = styled(Modal)`
  width: calc(100% - 60px);
  height: calc(100% - 60px);
`;
const MapTip = styled.div`
  position: absolute;
  top: 50%;
  left: 50%;
  margin-right: -50%;
  transform: translate(-50%, -50%);
`;

function MapModal({ defaultLocation = {}, onRequestClose, onConfirm }: Props) {
  const { zoom = 13, lng, lat } = defaultLocation;
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<AMap.Map>();
  const markerRef = useRef<AMap.Marker>();
  const input = useInput<HTMLInputElement>();
  const tip = useToggle(false);
  const searchDrop = useRef<HTMLDivElement>(null);

  const setPoint = (pos: AMap.LngLat) => {
    tip.off();
    if (markerRef.current) markerRef.current?.setMap(null);
    markerRef.current = new AMap.Marker({
      // @ts-ignore
      position: pos,
      map: mapRef.current,
    });
  };

  useEffect(() => {
    if (!containerRef.current) return;
    mapRef.current = new AMap.Map(containerRef.current, { zoom });
    const handleInit = () => {
      if (lng && lat) {
        const pos = new AMap.LngLat(lng, lat);
        setPoint(pos);
        mapRef.current?.setZoomAndCenter(zoom, pos, true);
      } else {
        tip.on();
      }
    };
    const handleClick = (e: any) => {
      setPoint(e.lnglat);
    };
    mapRef.current.on('complete', handleInit);
    mapRef.current.on('click', handleClick);

    // @ts-ignore
    const auto = new AMap.AutoComplete({
      input: input.ref.current,
      output: searchDrop.current,
    });
    // @ts-ignore
    const placeSearch = new AMap.PlaceSearch();
    // @ts-ignore
    const select = (e) => {
      placeSearch.setCity(e.poi.adcode);
      // @ts-ignore
      placeSearch.search(e.poi.name, (_, results) => {
        const poi = results?.poiList?.pois?.[0];
        if (poi) {
          mapRef.current?.setCenter(poi.location, true);
          setPoint(poi.location);
        }
      });
    };
    auto.on('select', select);

    return () => {
      mapRef.current?.off('complete', handleInit);
      mapRef.current?.off('click', handleClick);
      mapRef.current?.destroy();
    };
  }, []);

  const handleConfirm = () => {
    if (markerRef.current?.getMap()) {
      const pos = markerRef.current?.getPosition();
      if (pos) {
        onConfirm({
          lng: pos.lng,
          lat: pos.lat,
          zoom: mapRef.current?.getZoom() || 13,
        });
        onRequestClose();
        return;
      }
    }
    onConfirm({});
    onRequestClose();
  };

  const renderHeader = () => {
    return (
      <>
        <Title>添加导航</Title>
        <div className="relative ml-20 h-30 w-200">
          <Input
            className="h-30 w-full"
            placeholder="请输入地名"
            {...input.bind}
          />
          <div
            className="absolute z-30 mt-5 w-full select-none overflow-hidden rounded-xs bg-white focus:outline-none"
            ref={searchDrop}
          />
        </div>
      </>
    );
  };
  const renderFooter = () => {
    return (
      <>
        <Button onClick={handleConfirm}>确认操作</Button>
        <TextLink
          className="absolute right-0"
          onClick={() => {
            markerRef.current?.setMap(null);
            input.clear();
            tip.on();
          }}
        >
          清除坐标
        </TextLink>
      </>
    );
  };
  return (
    <StyledModal
      isOpen
      onRequestClose={onRequestClose}
      header={renderHeader()}
      footer={renderFooter()}
    >
      <div className="h-full" ref={containerRef} />
      {tip.isOn && (
        <MapTip className="rounded bg-black-black bg-opacity-50 py-5 px-10 text-white">
          点击任意位置进行标注
        </MapTip>
      )}
    </StyledModal>
  );
}

export default MapModal;
